import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useCurrentUserQuery } from 'http-client/hooks/authHooks';
import { MeDto } from 'http-client/types/auth.types';
import { HttpClient } from 'http-client/apiClient';
interface PermissionContextValue {
  user: MeDto | null;
  permissions: Set<string>;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hasToken = Boolean(HttpClient.getAuthCredentials()?.token);

  const { data: response, isLoading } = useCurrentUserQuery({
    enabled: hasToken,
  });

  const user = response?.success ? response.data ?? null : null;

  const permissions = useMemo(
    () => new Set((user?.permissions ?? []).map((p) => p.toLowerCase())),
    [user?.permissions],
  );

  const hasPermission = useCallback(
    (permission: string) => permissions.has(permission.toLowerCase()),
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (keys: string[]) => keys.some((key) => hasPermission(key)),
    [hasPermission],
  );

  const hasAllPermissions = useCallback(
    (keys: string[]) => keys.every((key) => hasPermission(key)),
    [hasPermission],
  );

  const value = useMemo<PermissionContextValue>(
    () => ({
      user: user ?? null,
      permissions,
      isLoading: hasToken && isLoading,
      isAuthenticated: hasToken,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    }),
    [
      user,
      permissions,
      hasToken,
      isLoading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    ],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
